import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';

import { getApiBaseUrl } from '../config/apiConfig';

const useHashRouter = process.env.REACT_APP_USE_HASH_ROUTER === 'true';

const API_BASE_URL = getApiBaseUrl();
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for sending HttpOnly cookies
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // Add timeout to prevent hanging requests
});

// Request interceptor to add auth headers if needed
axiosInstance.interceptors.request.use(
  (config) => {
    // Add any auth headers here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for better error handling and 401 redirect
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const data = error.response.data as { message?: string; Message?: string; error?: string } | undefined;
      const msg = data?.message ?? data?.Message ?? data?.error ?? '';
      if (/invalid admin password|incorrect password|wrong password|invalid password/i.test(msg)) {
        return Promise.reject(error);
      }
      if (/deactivated|account is deactivated/i.test(msg)) {
        try {
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          localStorage.removeItem('authToken');
          sessionStorage.removeItem('authToken');
        } catch {
          /* ignore */
        }
      }
      const currentPath = useHashRouter ? window.location.hash : window.location.pathname;
      const isOnLogin = useHashRouter ? currentPath === '#/login' : currentPath === '/login';
      if (!isOnLogin) {
        console.warn('Unauthorized (401) - Redirecting to login page');
        window.location.href = useHashRouter ? '#/login' : '/login';
      }
      return Promise.reject(error);
    }

    // Handle other errors
    if (error.code === 'ERR_NETWORK') {
      console.error('Network Error: Unable to connect to the server');
    } else if (error.code === 'ECONNABORTED') {
      console.error('Request timeout');
    }
    return Promise.reject(error);
  }
);

// Generic request function
async function request<T>(config: AxiosRequestConfig): Promise<any> {
  try {
    const response: AxiosResponse<T> = await axiosInstance(config);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      console.error('API Error:', error.response.status, error.response.data);
      const body = error.response.data;
      if (body && typeof body === 'object' && !Array.isArray(body)) {
        throw { ...(body as object), status: error.response.status };
      }
      throw { message: body, status: error.response.status };
    } else if (axios.isAxiosError(error) && error.request) {
      console.error('Network Error:', error.message);
      throw { message: 'Network error - unable to connect to server' };
    } else {
      console.error('Unexpected Error:', error.message);
      throw { message: 'Unexpected error occurred' };
    }
  }
}

// Exported API methods
const apiService = {
  get: <T>(url: string, config?: AxiosRequestConfig) => request<T>({ method: 'GET', url, ...config }),

  post: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    request<T>({ method: 'POST', url, data, ...config }),

  put: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    request<T>({ method: 'PUT', url, data, ...config }),

  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    request<T>({ method: 'PATCH', url, data, ...config }),

  delete: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    request<T>({ method: 'DELETE', url, data, ...config }),

  /** GET a binary/blob endpoint. Returns the full AxiosResponse so callers can read headers. */
  getBlob: async (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<Blob>> => {
    try {
      return await axiosInstance.get<Blob>(url, {
        ...config,
        responseType: 'blob',
        timeout: 120000,
      });
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response) {
        const data = error.response.data;
        if (data instanceof Blob && data.type?.includes('json')) {
          const text = await data.text();
          try { throw JSON.parse(text); } catch (parsed) { throw parsed; }
        }
        throw { error: `Server error (${error.response.status})` };
      } else if (axios.isAxiosError(error) && error.request) {
        throw { error: 'Network error - unable to connect to server' };
      }
      throw { error: 'Unexpected error occurred' };
    }
  },
};

export default apiService;
